import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoreUrl } from '../utils/storeUrls';
import { Check } from 'lucide-react';

const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');

  const handleDownload = () => {
    window.open(getStoreUrl(i18n.language), '_blank');
  };

  const handleContactSales = () => {
    window.location.href = '/contact';
  };

  const plans = [
    {
      name: t('pricing.plans.free.name', 'Free'),
      price: t('pricing.plans.free.price', '$0'),
      period: t('pricing.plans.free.period', 'forever'),
      features: Array.isArray(t('pricing.plans.free.features', { returnObjects: true }))
        ? (t('pricing.plans.free.features', { returnObjects: true }) as string[])
        : ['10 min of recording a day', '30 languages', 'Bullet-style summaries', 'Markdown export'],
      cta: t('pricing.plans.free.cta', 'Get Started'),
      highlighted: false,
      badge: null as string | null,
    },
    {
      name: 'Premium',
      price: billing === 'monthly'
        ? t('pricing.plans.standard.price', '$7.99')
        : t('pricing.plans.standard.priceAnnual', '$4.17'),
      period: t('pricing.plans.standard.period', 'per month'),
      billedNote: billing === 'annual'
        ? t('pricing.plans.standard.priceAnnualNote', 'billed as $49.99/year')
        : null,
      features: Array.isArray(t('pricing.plans.standard.features', { returnObjects: true }))
        ? (t('pricing.plans.standard.features', { returnObjects: true }) as string[])
        : ['1,800 min/month', 'All 30 languages + live translate', 'Smart summaries — flashcards, quizzes, meetings, todos & more', 'Premium AI models (AssemblyAI, GPT-4o) with speaker diarization', 'Privacy mode — fully device-only processing'],
      cta: t('pricing.plans.standard.cta', 'Get Started'),
      highlighted: true,
      badge: t('pricing.plans.standard.badge', 'Most popular') as string | null,
    },
    {
      name: t('pricing.plans.enterprise.name', 'Enterprise'),
      price: t('pricing.plans.enterprise.price', 'Custom'),
      period: t('pricing.plans.enterprise.period', 'per organization'),
      features: Array.isArray(t('pricing.plans.enterprise.features', { returnObjects: true }))
        ? (t('pricing.plans.enterprise.features', { returnObjects: true }) as string[])
        : ['Custom AI models', 'Shared vocab + templates', 'DPA, BAA, on-prem option', 'Priority support'],
      cta: t('pricing.plans.enterprise.cta', 'Contact Sales'),
      highlighted: false,
      badge: null as string | null,
    },
  ];

  return (
    <main className="px-4 py-16" style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)', minHeight: '100vh' }}>
      <section className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4" style={{ color: 'var(--v2-ink)' }}>{t('pricing.title')}</h1>
        <p className="text-xl mb-8" style={{ color: 'var(--v2-ink-muted)' }}>
          {t('pricing.subtitle')}
        </p>

        {/* Billing toggle */}
        <div className="inline-flex rounded-full p-1" style={{ background: 'var(--v2-card)', border: '1px solid var(--v2-line)', borderRadius: '999px', padding: '4px' }}>
          <button
            onClick={() => setBilling('monthly')}
            className="px-5 py-2 rounded-full text-sm font-medium transition-colors"
            style={billing === 'monthly'
              ? { background: 'var(--v2-primary)', color: '#fff' }
              : { color: 'var(--v2-ink-muted)' }}
          >
            {t('pricing.toggle.monthly', 'Monthly')}
          </button>
          <button
            onClick={() => setBilling('annual')}
            className="px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2"
            style={billing === 'annual'
              ? { background: 'var(--v2-primary)', color: '#fff' }
              : { color: 'var(--v2-ink-muted)' }}
          >
            {t('pricing.toggle.annual', 'Annually')}
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--v2-primary)', color: '#fff', opacity: 0.85 }}>
              {t('pricing.toggle.save', 'Save 20%')}
            </span>
          </button>
        </div>
      </section>

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-start">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative rounded-2xl p-8 ${plan.highlighted ? 'shadow-2xl md:scale-105' : ''}`}
            style={plan.highlighted
              ? { background: 'var(--v2-primary)', border: '1px solid var(--v2-primary)', color: '#fff' }
              : { background: 'var(--v2-card)', border: '1px solid var(--v2-line)' }}
          >
            {plan.badge && (
              <div
                className="absolute -top-4 left-1/2 -translate-x-1/2 text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap"
                style={plan.highlighted
                  ? { background: '#fff', color: 'var(--v2-primary)', border: 'none' }
                  : { background: 'var(--v2-card)', color: 'var(--v2-primary)', border: '1px solid var(--v2-line)' }}
              >
                {plan.badge}
              </div>
            )}
            <h2 className="text-2xl font-bold mb-4" style={{ color: plan.highlighted ? '#fff' : 'var(--v2-ink)' }}>{plan.name}</h2>
            <div className="mb-6">
              <span className="text-4xl font-bold" style={{ color: plan.highlighted ? '#fff' : 'var(--v2-ink)' }}>{plan.price}</span>
              <span className="ml-1 text-sm" style={{ color: plan.highlighted ? 'rgba(255,255,255,0.65)' : 'var(--v2-ink-muted)' }}>/{plan.period}</span>
              {'billedNote' in plan && plan.billedNote && (
                <div style={{ fontSize: '12px', color: plan.highlighted ? 'rgba(255,255,255,0.6)' : 'var(--v2-ink-muted)', marginTop: '4px' }}>
                  {plan.billedNote}
                </div>
              )}
            </div>
            <ul className="mb-8 space-y-3">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <Check
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: plan.highlighted ? '#fff' : 'var(--v2-primary)' }}
                  />
                  <span className="text-sm" style={{ color: plan.highlighted ? 'rgba(255,255,255,0.85)' : 'var(--v2-ink-muted)' }}>{feature}</span>
                </li>
              ))}
            </ul>
            <button
              className="w-full py-3 px-6 rounded-full font-bold transition-colors"
              style={plan.highlighted
                ? { background: '#fff', color: 'var(--v2-primary)' }
                : { background: 'var(--v2-primary)', color: '#fff' }}
              onClick={() => {
                if (plan.cta === t('pricing.plans.enterprise.cta', 'Contact Sales')) {
                  handleContactSales();
                } else {
                  handleDownload();
                }
              }}
            >
              {plan.cta}
            </button>
          </div>
        ))}
      </div>
    </main>
  );
};

export default PricingPage;
